import { lazy } from 'react'
import { Route, Routes } from 'react-router-dom'

import { DashboardLayout } from '@/layouts'

/**
 * Every page is code-split. The dashboard is the landing route, so it is the
 * one chunk worth prefetching eagerly; the rest download on navigation.
 *
 * `Suspense` and the error boundary live in `DashboardLayout`, which keeps the
 * shell mounted while a chunk loads or a page throws.
 */
const DashboardPage = lazy(async () => ({
  default: (await import('@/app/dashboard')).DashboardPage,
}))
const OrcamentosPage = lazy(async () => ({
  default: (await import('@/app/orcamentos')).OrcamentosPage,
}))
const QuoteEditorPage = lazy(async () => ({
  default: (await import('@/app/orcamentos/editor')).QuoteEditorPage,
}))
const QuoteBuilderProRoutePage = lazy(async () => ({
  default: (await import('@/app/orcamentos/pro')).QuoteBuilderProRoutePage,
}))
const QuoteWorkspaceRoutePage = lazy(async () => ({
  default: (await import('@/app/orcamentos/workspace')).QuoteWorkspaceRoutePage,
}))
const SolutionBuilderRoutePage = lazy(async () => ({
  default: (await import('@/app/solution-builder')).SolutionBuilderRoutePage,
}))
const KnowledgeBaseAdminPage = lazy(async () => ({
  default: (await import('@/app/knowledge-base')).KnowledgeBaseAdminPage,
}))
const RecommendationPage = lazy(async () => ({
  default: (await import('@/app/recomendacoes')).RecommendationPage,
}))
const ClientesPage = lazy(async () => ({
  default: (await import('@/app/clientes')).ClientesPage,
}))
const ProdutosPage = lazy(async () => ({
  default: (await import('@/app/produtos')).ProdutosPage,
}))
const SearchPage = lazy(async () => ({
  default: (await import('@/app/search')).SearchPage,
}))
const ComparadorPage = lazy(async () => ({
  default: (await import('@/app/comparador')).ComparadorPage,
}))
const ComparadorInteligentePage = lazy(async () => ({
  default: (await import('@/app/comparador-inteligente')).ComparadorInteligentePage,
}))
const OtimizadorPage = lazy(async () => ({
  default: (await import('@/app/otimizador')).OtimizadorPage,
}))
const IAPage = lazy(async () => ({
  default: (await import('@/app/ia')).IAPage,
}))
const SalesWorkbenchPage = lazy(async () => ({
  default: (await import('@/app/sales-workbench')).SalesWorkbenchPage,
}))
const SettingsCenterPage = lazy(async () => ({
  default: (await import('@/app/settings')).SettingsCenterPage,
}))
const IntegrationCenterPage = lazy(async () => ({
  default: (await import('@/app/integrations')).IntegrationCenterPage,
}))
const SystemStatusPage = lazy(async () => ({
  default: (await import('@/app/system')).SystemStatusPage,
}))
const InteligenciaProdutoPage = lazy(async () => ({
  default: (await import('@/app/inteligencia-produto')).InteligenciaProdutoPage,
}))
const HistoricoPage = lazy(async () => ({
  default: (await import('@/app/historico')).HistoricoPage,
}))
const OportunidadesPage = lazy(async () => ({
  default: (await import('@/app/oportunidades')).OportunidadesPage,
}))
const ConfiguracoesPage = lazy(async () => ({
  default: (await import('@/app/configuracoes')).ConfiguracoesPage,
}))
const PrecificacaoPage = lazy(async () => ({
  default: (await import('@/app/configuracoes/precificacao')).PrecificacaoPage,
}))
const SuppliersPage = lazy(async () => ({
  default: (await import('@/app/configuracoes/fornecedores')).SuppliersPage,
}))
const SupplierDetailPage = lazy(async () => ({
  default: (await import('@/app/configuracoes/fornecedores/[id]')).SupplierDetailPage,
}))
const IntegracoesPage = lazy(async () => ({
  default: (await import('@/app/configuracoes/integracoes')).IntegracoesPage,
}))
const AutomacoesPage = lazy(async () => ({
  default: (await import('@/app/configuracoes/automacoes')).AutomacoesPage,
}))

const ForbiddenPage = lazy(async () => ({
  default: (await import('@/app/errors')).ForbiddenPage,
}))
const NotFoundPage = lazy(async () => ({
  default: (await import('@/app/errors')).NotFoundPage,
}))
const ServerErrorPage = lazy(async () => ({
  default: (await import('@/app/errors')).ServerErrorPage,
}))
const UnauthorizedPage = lazy(async () => ({
  default: (await import('@/app/errors')).UnauthorizedPage,
}))

export function AppRouter() {
  return (
    <Routes>
      <Route element={<DashboardLayout />}>
        <Route index element={<DashboardPage />} />
        <Route path="orcamentos" element={<OrcamentosPage />} />
        <Route path="orcamentos/editor" element={<QuoteEditorPage />} />
        <Route path="orcamentos/editor/:id" element={<QuoteEditorPage />} />
        <Route path="orcamentos/pro" element={<QuoteBuilderProRoutePage />} />
        <Route path="orcamentos/workspace" element={<QuoteWorkspaceRoutePage />} />
        <Route path="solution-builder" element={<SolutionBuilderRoutePage />} />
        <Route path="configurador" element={<SolutionBuilderRoutePage />} />
        <Route path="knowledge-base" element={<KnowledgeBaseAdminPage />} />
        <Route path="base-conhecimento" element={<KnowledgeBaseAdminPage />} />
        <Route path="recomendacoes" element={<RecommendationPage />} />
        <Route path="clientes" element={<ClientesPage />} />
        <Route path="produtos" element={<ProdutosPage />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="comparador" element={<ComparadorPage />} />
        <Route path="comparador-inteligente" element={<ComparadorInteligentePage />} />
        <Route path="otimizador" element={<OtimizadorPage />} />
        <Route path="ia" element={<IAPage />} />
        <Route path="sales-workbench" element={<SalesWorkbenchPage />} />
        <Route path="settings" element={<SettingsCenterPage />} />
        <Route path="integrations" element={<IntegrationCenterPage />} />
        <Route path="system" element={<SystemStatusPage />} />
        <Route path="inteligencia-produto" element={<InteligenciaProdutoPage />} />
        <Route path="historico" element={<HistoricoPage />} />
        <Route path="oportunidades" element={<OportunidadesPage />} />
        <Route path="configuracoes" element={<ConfiguracoesPage />} />
        <Route path="configuracoes/precificacao" element={<PrecificacaoPage />} />
        <Route path="configuracoes/fornecedores" element={<SuppliersPage />} />
        <Route path="configuracoes/fornecedores/:id" element={<SupplierDetailPage />} />
        <Route path="configuracoes/integracoes" element={<IntegracoesPage />} />
        <Route path="configuracoes/automacoes" element={<AutomacoesPage />} />

        {/* Relative paths: the parent is a pathless layout route. */}
        <Route path="401" element={<UnauthorizedPage />} />
        <Route path="403" element={<ForbiddenPage />} />
        <Route path="500" element={<ServerErrorPage />} />
        <Route path="404" element={<NotFoundPage />} />

        {/*
          Previously this redirected to "/", which hid broken links behind a
          silent bounce to the dashboard. Rendering 404 in place keeps the URL
          and tells the user what happened.
        */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  )
}
